import { useCallback, useMemo } from 'react';

interface NotificationTracking {
  showCount: number;
  lastShown: number | null;
  acknowledged: boolean;
  firstShown: number | null;
}

const STORAGE_KEY_PREFIX = 'toolbar_notification_';
const DAYS_TO_MS = 24 * 60 * 60 * 1000;

/**
 * Get a quasi-random delay based on show count
 * Returns number of days to wait before showing again
 */
const getQuasiRandomDelay = (showCount: number): number => {
  // Based on show count, return days to wait
  const delays = [1, 2, 3]; // Days to wait for retry
  // Use show count and current date for pseudo-randomness
  const seed = showCount + new Date().getDate();
  return delays[seed % delays.length];
};

/**
 * Hook for tracking notification display and acknowledgment
 * Manages localStorage tracking and timing logic for smart retry
 */
export const useNotificationTracking = (notificationId: string) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${notificationId}`;

  const getTracking = useCallback((): NotificationTracking => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        return { 
          showCount: 0, 
          lastShown: null, 
          acknowledged: false, 
          firstShown: null 
        };
      }
      return JSON.parse(stored) as NotificationTracking;
    } catch {
      // If localStorage fails or data is corrupted, return defaults
      return { 
        showCount: 0, 
        lastShown: null, 
        acknowledged: false, 
        firstShown: null 
      };
    }
  }, [storageKey]);

  const updateTracking = useCallback((updates: Partial<NotificationTracking>) => {
    try {
      const current = getTracking();
      const updated = { ...current, ...updates };
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }, [storageKey, getTracking]);

  const recordShowing = useCallback(() => {
    const current = getTracking();
    updateTracking({
      showCount: current.showCount + 1,
      lastShown: Date.now(),
      firstShown: current.firstShown || Date.now(),
    });
  }, [getTracking, updateTracking]);

  const shouldShowBasedOnTiming = useCallback((): boolean => {
    const tracking = getTracking();
    
    // Don't show if already acknowledged
    if (tracking.acknowledged) {
      return false;
    }
    
    // Don't show if we've already shown 3 times
    if (tracking.showCount >= 3) {
      return false;
    }
    
    // Show if never shown before
    if (!tracking.lastShown) {
      return true;
    }
    
    // Calculate days since last shown
    const daysSince = (Date.now() - tracking.lastShown) / DAYS_TO_MS;
    const requiredDelay = getQuasiRandomDelay(tracking.showCount);
    
    // Show if enough time has passed
    return daysSince >= requiredDelay;
  }, [getTracking]);

  const setAcknowledged = useCallback(() => {
    updateTracking({ acknowledged: true });
  }, [updateTracking]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Silently fail
    }
  }, [storageKey]);

  // Memoized tracking data
  const tracking = useMemo(() => getTracking(), [getTracking]);

  return {
    tracking,
    recordShowing,
    shouldShowBasedOnTiming,
    setAcknowledged,
    reset,
  };
};